import { getTareaByID } from '@/helpers/api/tasks'
import { getRdiByID } from '@/helpers/api/rdi'
import { getEntregaByID, getConfig } from '@/helpers/api/entrega'
import { getJobTypes } from '@/helpers/api/user'

/**
 * Mixin para manejar clicks en elementos .assoc-btn generados por el backend
 * (menciones/referencias a tareas, RDIs, adicionales, entregas).
 *
 * Uso:
 *   1. Agregar el mixin: mixins: [assocClickMixin]
 *   2. En mounted:      this.$_bindAssocClick(this.$el) o un $ref específico
 *   3. En beforeDestroy: this.$_unbindAssocClick(el)
 *
 * Hooks sobreescribibles:
 *   - beforeAssocNavigate(): se llama antes de navegar
 *   - afterAssocNavigate(id, type): se llama después de navegar
 */
export const assocClickMixin = {
  methods: {
    // Hook pre-navegación (sobreescribir en el componente si hace falta)
    beforeAssocNavigate () {},
    // Hook post-navegación (sobreescribir en el componente si hace falta)
    afterAssocNavigate (id, type) {}, // eslint-disable-line no-unused-vars

    $_bindAssocClick (el) {
      if (!el) return
      el.__assocClickFn__ = (e) => this.$_onAssocClick(e)
      el.__assocKeydownFn__ = (e) => this.$_onAssocKeydown(e)
      el.addEventListener('click', el.__assocClickFn__)
      el.addEventListener('keydown', el.__assocKeydownFn__)
    },

    $_unbindAssocClick (el) {
      if (!el) return
      if (el.__assocClickFn__) {
        el.removeEventListener('click', el.__assocClickFn__)
        delete el.__assocClickFn__
      }
      if (el.__assocKeydownFn__) {
        el.removeEventListener('keydown', el.__assocKeydownFn__)
        delete el.__assocKeydownFn__
      }
    },

    $_onAssocClick (e) {
      const el = e.target && e.target.closest ? e.target.closest('.assoc-btn') : null
      if (!el) return
      e.preventDefault()
      // Evitar que el evento burbujee a otros listeners delegados en ancestros
      e.stopPropagation()
      this.handleAssocElement(el)
    },

    $_onAssocKeydown (e) {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return
      const active = document.activeElement
      const el = active && active.classList && active.classList.contains('assoc-btn')
        ? active
        : (active && active.closest ? active.closest('.assoc-btn') : null)
      if (!el) return
      e.preventDefault()
      this.handleAssocElement(el)
    },

    async handleAssocElement (el) {
      await this.beforeAssocNavigate()
      const id = (el && el.dataset && el.dataset.id) ? el.dataset.id : (el ? el.getAttribute('data-id') : null)
      const type = (el && el.dataset && el.dataset.type) ? el.dataset.type : (el ? el.getAttribute('data-type') : null)
      if (type === 'task') {
        this.viewAssocTarea(parseInt(id))
      } else if (type === 'rdi') {
        this.viewAssocRDI(parseInt(id))
      } else if (type === 'additional') {
        this.viewAssocAdditional(parseInt(id))
      } else if (type === 'entrega') {
        this.viewAssocEntrega(parseInt(id))
      } else if (type === 'agp') {
        this.$router.push({ name: 'Adicionales', query: { listing_type: 3 } })
      } else {
        console.log('tipo de asociación desconocido', type)
      }
      this.afterAssocNavigate(id, type)
    },

    async viewAssocTarea (task_id) {
      this.$store.consultasStore.commit('setCurrentTarea', { loading: true })
      const tarea = await getTareaByID({ task_id })
      if (tarea.code === 200) {
        const tareaData = tarea.task
        tareaData.prev_seen_by_me = false
        this.$store.consultasStore.commit('setCurrentTarea', tareaData)
      }
    },

    async viewAssocRDI (rdi_id) {
      this.$store.rdiStore.commit('setCurrentRDI', { loading: true })
      const rdi = await getRdiByID({ rdi_id })
      if (rdi.code === 200) {
        const rdiData = rdi.rdi
        rdiData.prev_seen_by_me = false
        this.$store.rdiStore.commit('setCurrentRDI', rdiData)
      }
    },

    viewAssocAdditional (additional_id) {
      this.$store.adicionalesStore.commit('setCurrentAdicional', { loading: true })
      this.$store.adicionalesStore.dispatch('getAdditionalByID', {
        additional_id,
        seen_by_me: false
      })
    },

    async viewAssocEntrega (entregaId) {
      const selectedProject = this.$store.projectsStore.state.selectedProject
      if (!selectedProject) return
      const projectID = selectedProject.id
      this.$store.entregaStore.commit('setCurrentEntrega', { loading: true })
      try {
        const entregaResponse = await getEntregaByID({ entrega_id: entregaId })
        const [configResponse, jobTypesResponse] = await Promise.all([
          getConfig({ project_id: projectID }),
          getJobTypes({ project_id: projectID })
        ])
        this.$store.usersStore.commit('setJobTypes', jobTypesResponse.job_types)
        this.$store.entregaStore.commit('setConfig', configResponse.config)
        if (entregaResponse.code === 403) {
          this.$store.entregaStore.commit('setCurrentEntrega', null)
        } else if (entregaResponse.code === 200) {
          this.$store.entregaStore.commit('setCurrentEntrega', entregaResponse.entrega)
        }
      } catch (error) {
        this.$store.entregaStore.commit('setCurrentEntrega', { loading: false, error: true })
      }
    }
  }
}
